import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'store';
import { setLeadData } from 'leadData/leadSlice';

import { Formik } from 'formik';
import * as Yup from 'yup';
import { format, differenceInYears } from 'date-fns';
import { SEX } from 'v3/utils/constants';
import { toast } from 'react-toastify';

import { ContractFlowClient } from 'v3/api/client/ContractFlowClient';

import { useQuery } from 'v3/hooks/useQuery';
import { useMixpanel } from 'v3/hooks/useMixpanel';
import { trackMeta } from 'utils/metaPixel';
import { handleErrorMessage, useError } from 'v3/utils/errorHandler';
// import { useIntercom } from 'hooks/useIntercom';

import Field from 'v3/components/Field';
import Select from 'v3/components/Select';
import Checkbox from 'v3/components/Checkbox';
import Steps from 'v3/components/Steps';
import Button from 'v3/components/Button';
import ErrorFocus from 'v3/components/ErrorFocus';
import { Search } from '@styled-icons/remix-line/Search';
import { PriceTag3 } from '@styled-icons/remix-line';
import { Lock } from 'lucide-react';
import DiscountToast from 'v3/components/DiscountToast';
import CopyCode from 'v3/components/CopyCode';
import theme from 'styles/theme';

import * as S from './styles';
import * as G from './gettingStartedStyles';
import RadixIconsEyeOpen from 'v3/components/Icons/RadixIconsEyeOpen';
import RadixIconsEyeClosed from 'v3/components/Icons/RadixIconsEyeClosed';
import { useIsRealEstateClubId } from 'v3/hooks/useIsRealEstateClubId';
import { sanitizeInput } from 'v3/utils/sanitizeFields';
import PlacesAutocompleteInput from 'v3/components/PlacesAutoCompleteInput';
import PhoneCountrySelect from 'v3/components/PhoneCountrySelect';
import {
  DEFAULT_PHONE_COUNTRY,
  PHONE_COUNTRIES,
  PhoneCountryCode,
} from 'v3/utils/phoneCountries';
import GettingStarted from './GettingStarted';


const ToastContent = () => {
  return (
    <S.ToastWrapper>
      <p>
        If this is your email, check your inbox to access the verification
        instructions for the existing Savings Club account.
      </p>
      <span>
        Never used it?{' '}
        <a
          href="https://savings.club/contact-us/?source=start-deal-email-already-in-use"
          rel="noreferrer"
          target="_blank"
        >
          Contact us now
        </a>
      </span>
    </S.ToastWrapper>
  );
};

const DISCOUNT_CODE = 'savemore';

const StartDeal = () => {
  const { leadData, wasDiscountShown } = useSelector(
    (state: RootState) => state.lead
  );
  const query = useQuery();
  const navigate = useNavigate();
  const formikRef = useRef<any>(null);
  const testFlag = query.get('test') ?? '';
  // Uppercased at the source: this seeds the form field, the auto-validation effect
  // and the submitted body, and the backend looks codes up with an exact match — so
  // ?referralCode=fee20 would otherwise 404 on a code that exists.
  const referralCodeParam = (query.get('referralCode') ?? '').toUpperCase();
  const clubId = query.get('clubId') ?? '';
  const paymentOption = query.get('paymentOption') ?? '';
  const term = query.get('months') ?? '84';
  const downPayment = query.get('downPayment') ?? 0;
  const [usedEmail, setUsedEmail] = useState(false);
  const [referralCodeIsApplied, setReferralCodeIsApplied] = useState(false);
  const [referralCodeDescription, setReferralCodeDescription] = useState('');
  // const [activateIntercom, setActivateIntercom] = useState(false);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const mp = useMixpanel();
  const cfUuid = query.get('cfUuid');
  const initialEmail = query.get('email');
  const [line1, setLine1] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [openPromotionalModal, setOpenPromotionalModal] = useState(false);
  const [wasPromotionalModalShown, setWasPromotionalModalShown] =
    useState(false);
  // useIntercom(activateIntercom);

  const [fieldDisabled, setFieldDisabled] = useState(false);
  const [isAddressUpdated, setIsAddressUpdated] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [ssnInputType, setSsnInputType] = useState('password');
  const [phoneCountry, setPhoneCountry] = useState<PhoneCountryCode>(
    DEFAULT_PHONE_COUNTRY
  );
  const initialValues = {
    firstName: '',
    middleName: '',
    lastName: '',
    sex: '',
    dateOfBirth: '',
    userPhone: '',
    email: initialEmail || '',
    location: '',
    postalCode: '',
    line2: '',
    socialSecurityNumber: '',
    referralCode: referralCodeParam,
    notice1: '',
    /* Boolean, not '' like notice1. notice1 can afford a string because Yup blocks
       submission until it is true, so the server never sees the empty case. This one
       reaches the server unchecked by definition, and an '' would fail a boolean
       field for exactly the people who declined. */
    smsConsent: false,
  };

  const onlyLetters = /^[a-zA-ZÀ-ÖØ-öø-ÿ\s']*(?:-[a-zA-ZÀ-ÖØ-öø-ÿ\s']+)*$/;

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        firstName: Yup.string()
          .trim()
          .matches(onlyLetters, 'The field must contain only letters')
          .required('This field is required'),
        middleName: Yup.string()
          .trim()
          .matches(onlyLetters, 'The field must contain only letters'),
        lastName: Yup.string()
          .trim()
          .matches(onlyLetters, 'The field must contain only letters')
          .required('This field is required'),
        sex: Yup.string().required('This field is required'),
        dateOfBirth: Yup.date()
          .required('This field is required')
          .typeError('Date must be in the format MM/DD/YYYY')
          .test(
            'DOB',
            'Invalid date, you need to be at least 18 years old.',
            (value) => {
              return differenceInYears(new Date(), Number(value)) >= 18;
            }
          )
          .test('DOB', 'Please choose a valid date of birth.', (value) => {
            return differenceInYears(new Date(), Number(value)) <= 150;
          }),
        userPhone: Yup.string()
          .required('This field is required')
          .test('phone-digits', 'Invalid number', (value) => {
            const digits = (value ?? '').replace(/\D/g, '');
            return digits.length === PHONE_COUNTRIES[phoneCountry].digitCount;
          }),
        email: Yup.string()
          .email('Enter a valid email')
          .required('Email is required'),
        location: Yup.string()
          .required('This field is required')
          .min(5, 'This field should be at least 5 characters'),
        line2: Yup.string(),
        // Single consent checkbox: notice1 is the only FCRA authorization now.
        notice1: Yup.boolean()
          .required('This field is required')
          .oneOf([true], 'This field is required'),
        /* smsConsent is deliberately absent. An A2P/TCPA opt-in must never gate the
           submit: making it required is what got the number rejected with Twilio
           30505, "Agreeing to Receive Messages Must Be Optional". Formik carries the
           value without a schema entry. Do not "fix" this by adding one. */
      }),
    [phoneCountry]
  );

  const copyCouponToClipboard = () => {
    navigator.clipboard.writeText(DISCOUNT_CODE);
    toast.success('Copied');
  };

  // const handleSubscribeWaitlist = async (zipCode: string) => {
  //   const { firstName, lastName, email, clubId } = leadData;
  //   const body = {
  //     firstName,
  //     lastName,
  //     email,
  //     zipCode,
  //     group: clubId,
  //     referralCode: '',
  //   };

  //   mp.fireUserOnWaitingList();
  //   mp.registerWaitingList();

  //   await ContractFlowClient().subscribeToWaitingList({ body });

  //   navigate('/zip-unavailable');
  // };

  const isRealEstateClubId = useIsRealEstateClubId();

  useEffect(() => {
    if (mp.initialized && !cfUuid) {
      mp.fireAccessStartDeal();
    }
  }, [mp, cfUuid]);

  // useEffect(() => {
  //   if (!wasDiscountShown) {
  //     toast(<DiscountToast />, {
  //       autoClose: false,
  //       containerId: 'customPosition',
  //       closeOnClick: false,
  //     });
  //     dispatch(setLeadData({ wasDiscountShown: true }));
  //   }
  // }, [wasDiscountShown, dispatch]);

  useEffect(() => {
    const onExit = (e: MouseEvent) => {
      const isMouseOutside = e.clientY < 0 || e.clientY > window.innerHeight;

      if (isMouseOutside && !wasPromotionalModalShown) {
        setOpenPromotionalModal(false);
        setWasPromotionalModalShown(true);
      }
    };

    document.addEventListener('mouseleave', onExit);

    return () => {
      document.removeEventListener('mouseleave', onExit);
    };
  }, [wasPromotionalModalShown]);

  return (
    <>
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting, setFieldError }) => {

          setIsLoading(true);
          setUsedEmail(false);
          setFieldDisabled(true);

          Object.entries(values).forEach(([key, value]) => {
            values = { ...values, [key]: sanitizeInput(value) };
          });

          const address = {
            postalCode,
            state,
            city,
            country,
            line1,
            line2: values.line2,
          }

          const selectedPhoneCountry = PHONE_COUNTRIES[phoneCountry];
          const phoneDigits = values.userPhone.replace(/\D/g, '');
          const fullPhoneNumber = `${selectedPhoneCountry.dialCode}${phoneDigits}`;

          const body = {
            ...values,
            dateOfBirth: format(new Date(values.dateOfBirth), 'yyyy-MM-dd'),
            userPhone: fullPhoneNumber,
            userPhoneCountry: selectedPhoneCountry.code,
            address,
            dealValue: leadData.dealValue,
            groupId: (leadData.clubId || leadData.group?.id).toString(),
            half_payment_selected: paymentOption === 'half',
            months: term,
            down_payment: downPayment,
            ...(leadData.seats > 1 && { seats: leadData.seats }),
          };

          setSubmitting(true);

          const { data, error } = await ContractFlowClient().startDeal({
            body,
            testFlag,
          });

          if (error) {
            setFieldDisabled(false);
            const { getError } = useError(error)

            const emailError = getError("email");
            if (emailError) {
              setUsedEmail(true);
              toast.warn(emailError)
              setFieldError("email", emailError);
            }

            const postalCodeError = getError("address.postalCode");
            if (postalCodeError && postalCodeError === "This field may not be blank.") {
              console.log(postalCodeError);
              toast.warn("Please select an address with valid zip code.");
            } else {
               handleErrorMessage(error);
            }
            setIsLoading(false);
            setSubmitting(false);
          }

          if (data) {
            mp.registerStartDeal({
              ...data.result,
              state,
              city,
              zipCode: postalCode,
              phoneNumber: fullPhoneNumber,
              isSsnProvided: !!values.socialSecurityNumber,
            });

            // Lead, only past the backend's acceptance: name, email and phone
            // are real. A lead Meta counts should be a lead the CRM has.
            trackMeta('Lead');

            setUsedEmail(false);

            const queryTest = testFlag ? `&test=${testFlag}` : '';

            dispatch(
              setLeadData({
                leadData: {
                  ...leadData,
                  term,
                  firstName: values.firstName,
                  email: values.email,
                  address
                },
              })
            );

            if (!data.result.toWaitingList) {
              navigate(`?cfUuid=${data.result.contractFlowToken}${queryTest}`, {
                replace: true,
              });
            } else {
              navigate(
                `/waitlist/?cfUuid=${data.result.contractFlowToken}${queryTest}`,
                {
                  replace: true,
                }
              );
            }

          }
        }}
      >
        {(formik) => {
          const {
            errors,
            touched,
            values,
            setFieldValue,
            setFieldError,
            handleBlur,
          } = formik;

          useEffect(() => {
            if (touched.email && errors.email) {
              setUsedEmail(false);
            }
          }, [errors.email, touched.email]);

          const handleEmailNotification = () => {
            // setActivateIntercom(true);

            toast.warn(<ToastContent />, {
              toastId: 'email-already-in-use',
              closeOnClick: false,
              autoClose: false,
            });
          };

          const emailErrors = () => {
            if (errors.email === 'duplicated_email') {
              return (
                <>
                  Email already in use.{' '}
                  <Button
                    type="button"
                    variant="anchor"
                    size="small"
                    onClick={handleEmailNotification}
                  >
                    Click here
                  </Button>
                </>
              );
            }
            if (touched.email && errors.email) {
              return errors.email;
            }
          };

          const emailValidation = () => {
            if (!(touched.email && errors.email)) {
              if (!usedEmail) {
                return true;
              }
            }

            // dismiss the toast when email is good
            toast.dismiss('email-already-in-use');
            return false;
          };

          useEffect(() => {
            async function checkReferralCode() {
              await handleReferralCodeApplication(true);
            }
            if (referralCodeParam) {
              checkReferralCode();
            }
          }, [referralCodeParam]);

          const validateReferralCode = async (
            code: string,
            groupId: string
          ) => {
            if (code) {
              const { data, error } =
                await ContractFlowClient().getReferralCode(code, groupId);

              if (data) {
                setReferralCodeDescription(data.shortDescription);

                setReferralCodeIsApplied(true);
              }

              if (error) {
                setFieldError(
                  'referralCode',
                  error.message || 'Invalid referral code.'
                );
              }
            }
          };

          const handleReferralCodeApplication = async (isApplied: boolean) => {
            if (!isApplied) {
              setReferralCodeIsApplied(false);
              setReferralCodeDescription('');
              setFieldValue('referralCode', '');
              setFieldError('referralCode', '');

              return false;
            }

            await validateReferralCode(
              values.referralCode,
              leadData.clubId || clubId
            );
          };

          const autoCompleteSearch = document.querySelector(
            '.pac-container.pac-logo'
          );
          const fieldAutoComplete = document.querySelector('.pac-target-input');

          if (fieldAutoComplete && autoCompleteSearch) {
            fieldAutoComplete.append(autoCompleteSearch);
          }

          useEffect(() => {
            if(values.location?.startsWith('{"')) {
              const addressComponents = JSON.parse(values.location);
              const { line1: _line1, city: _city, state, postalCode: _postalCode, country } = addressComponents
              setLine1(_line1);
              setPostalCode(_postalCode);
              setCity(_city);
              setState(state);
              setCountry(country);
              setIsAddressUpdated(true);
            }
            else if (values.location.length === 0) {
              setFieldValue('line1', '');
              setPostalCode('');
              setCity('');
              setState('');
              setCountry('');
              setIsAddressUpdated(false);
            }
          }, [values.location]);

          useEffect(() => {
            if (showPassword) {
              setSsnInputType('text');
            } else {
              setSsnInputType('password');
            }
          }, [showPassword]);

          return (
            <GettingStarted
              values={values}
              errors={errors}
              touched={touched}
              setFieldValue={setFieldValue}
              phoneCountry={phoneCountry}
              setPhoneCountry={setPhoneCountry}
              isLoading={isLoading}
              onCtaClick={() => {
                /* The scroll used to be hand-rolled here. ErrorFocus now reaches the address
                   block through its `data-error-anchor`, so only the toast is left. */
                if (!line1) {
                  toast.warn('Please, inform your address.');
                }
              }}
            >
              <G.Divide />

              <G.Section>
                <G.SectionTitle>A bit more about you</G.SectionTitle>
                <G.DarkFields>
                  <S.FieldWrapper>
                    <Select
                      placeholder="Select gender"
                      name="sex"
                      label="Gender"
                      options={Object.entries(SEX).reverse()}
                      feedbackMessage={errors.sex}
                      isValid={!(touched.sex && errors.sex)}
                      required
                      disabled={fieldDisabled}
                    />
                  </S.FieldWrapper>

                  {/* The real input is inside <gmp-place-autocomplete>'s shadow root, so
                      ErrorFocus cannot query it — scroll to the wrapper instead. */}
                  <S.AddressContent data-error-anchor="location">
                    <S.FieldWrapper className="pac-target-input">
                      <Field
                        name="location"
                        label="Address line 1"
                        placeholder="Start typing your address"
                        required
                        feedbackMessage={errors.location}
                        isValid={!(touched.location && errors.location)}
                        disabled={fieldDisabled}
                        component={PlacesAutocompleteInput}
                      />
                      <G.FieldHint>
                        Used to check availability in your state and prepare
                        your offer.
                      </G.FieldHint>
                    </S.FieldWrapper>
                    <S.FieldWrapper>
                      <Field
                        name="line2"
                        label="Address line 2"
                        placeholder="Apartment, suite, etc."
                        itemType="search"
                        feedbackMessage={errors.line2}
                        isValid={!(touched.line2 && errors.line2)}
                        disabled={fieldDisabled}
                      />
                    </S.FieldWrapper>
                  </S.AddressContent>
                </G.DarkFields>
              </G.Section>

              {/* Original gate kept: when the email arrives via URL we don't ask for the SSN. */}
              {!initialEmail && (
                <G.Section>
                  <G.SectionTitle>
                    Do you have a Social Security number? (Optional)
                  </G.SectionTitle>
                  <G.SectionSubtitle>
                    We use it only to verify it&apos;s really you.
                  </G.SectionSubtitle>

                  <G.DarkFields>
                    <S.FieldWrapper className="ssn-field">
                      <S.LockIcon>
                        <Lock />
                      </S.LockIcon>
                      <Field
                        name="socialSecurityNumber"
                        mask="999-99-9999"
                        placeholder="e.g. 123-45-6789"
                        inputMode="numeric"
                        disabled={fieldDisabled}
                        type={ssnInputType}
                        rightContent={
                          <S.PasswordIcons
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <RadixIconsEyeOpen
                                style={{
                                  width: '26px',
                                  height: '26px',
                                  color: 'rgb(157, 164, 178)',
                                }}
                              />
                            ) : (
                              <RadixIconsEyeClosed
                                style={{
                                  width: '26px',
                                  height: '26px',
                                  color: 'rgb(157, 164, 178)',
                                }}
                              />
                            )}
                          </S.PasswordIcons>
                        }
                      />
                    </S.FieldWrapper>
                  </G.DarkFields>
                </G.Section>
              )}

              {/* The referral/promo input lives on ReviewOffer (step 1), next to the
                  discount it produces. A code passed by URL (?referralCode=) still
                  auto-applies through the effect, wherever the input sits. */}

              <G.Section>
                <G.FcraDisclaimer>
                  The credit information accessed for your pre-qualification
                  request may differ from the credit information a credit
                  grantor accesses at a later date.
                </G.FcraDisclaimer>

                {/* Single consent. This used to be two checkboxes required at the same
                    moment, gating the same action — one decision presented as two. Both
                    operative authorizations (the FCRA written instructions and the
                    "solely to pre-qualify" purpose limit) are stated here; the "may
                    differ" sentence from the second box became the disclaimer above. */}
                {/* The checkbox itself is a 0x0 opacity:0 input — anchor the scroll to the
                    visible box so the member lands on something they can see. */}
                <G.ConsentBox
                  $invalid={!!(touched.notice1 && errors.notice1)}
                  data-error-anchor="notice1"
                >
                  <Checkbox
                    name="notice1"
                    required
                    feedbackMessage={touched.notice1 && errors.notice1}
                    isValid={!(touched.notice1 && errors.notice1)}
                    disabled={fieldDisabled}
                  >
                    <>
                      I am providing &quot;written instructions&quot; to Savings
                      Club Financing Technologies LLC (&quot;Savings Club&quot;)
                      under the Fair Credit Reporting Act, authorizing Savings
                      Club to obtain information from my personal credit profile
                      or other information from the reseller, solely to
                      pre-qualify me for credit options.
                    </>
                  </Checkbox>
                </G.ConsentBox>

                {/* Optional by construction, and that is the whole point: the number
                    was rejected with Twilio 30505 because agreeing to SMS was not a
                    separate, skippable choice. No `required` (it renders a red
                    asterisk), no schema entry, no $invalid state, and the submit works
                    with the box untouched.

                    The fine print sits outside <Checkbox> on purpose. The component
                    wraps its children in a <label htmlFor>, so a link in there would
                    navigate and toggle the box on the same click. */}
                <G.ConsentBox>
                  <Checkbox name="smsConsent" disabled={fieldDisabled}>
                    <>
                      I want to receive text messages (SMS) from Savings Club.{' '}
                    </>
                  </Checkbox>
                  <G.SmsCheckboxHint>
                    Application updates, reminders, and offers.
                  </G.SmsCheckboxHint>
                  <G.SmsFinePrint>
                    Consent is not a condition of purchase. Message frequency
                    varies. Message and data rates may apply. Reply STOP to opt
                    out and HELP for help.{' '}
                    <a
                      href="https://savings.club/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms of Service
                    </a>
                    {' · '}
                    <a
                      href="https://savings.club/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>
                  </G.SmsFinePrint>
                </G.ConsentBox>
              </G.Section>

              <ErrorFocus />
            </GettingStarted>
          );
        }}
      </Formik>
      <S.PromotionalModal
        width={605}
        variant="secondary"
        title={
          <>
            Don't leave yet, <br /> <span>Get $10</span> OFF on Adm Fee
          </>
        }
        content={
          <S.PromotionalModalContent>
            <CopyCode
              code={DISCOUNT_CODE}
              variant="secondary"
              onClick={copyCouponToClipboard}
              icon={
                <PriceTag3 size={20} color={theme.colors.statusSuccess30} />
              }
            />
          </S.PromotionalModalContent>
        }
        onCancel={() => setOpenPromotionalModal(false)}
        open={openPromotionalModal}
      />
    </>
  );
};

export default StartDeal;
